import { useState } from 'react';
import {
  ActionIcon,
  Box,
  Code,
  CopyButton,
  Group,
  ScrollArea,
  SegmentedControl,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons';
import { JsonTree } from './json-tree';

type JsonView = 'tree' | 'raw';

interface JsonBlockProps {
  value: unknown;
  label?: string;
  maxHeight?: number;
}

function stringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function JsonBlock({ value, label, maxHeight = 480 }: JsonBlockProps) {
  const [view, setView] = useState<JsonView>('tree');
  const text = stringify(value);
  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Group position="apart" mb={4} noWrap>
        {label ? (
          <Text size="xs" color="dimmed" tt="uppercase" sx={{ letterSpacing: 0.4 }}>
            {label}
          </Text>
        ) : (
          <span />
        )}
        <Group spacing="xs" noWrap>
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(next) => setView(next as JsonView)}
            data={[
              { label: 'Tree', value: 'tree' },
              { label: 'Raw', value: 'raw' },
            ]}
          />
          <CopyButton value={text} timeout={1500}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy JSON'} withArrow>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color={copied ? 'green' : 'gray'}
                  onClick={copy}
                >
                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
      </Group>
      <ScrollArea.Autosize
        mah={maxHeight}
        type="auto"
        sx={(theme) => ({
          maxWidth: '100%',
          border: `1px solid ${theme.colors.gray[2]}`,
          borderRadius: theme.radius.sm,
          padding: theme.spacing.xs,
          backgroundColor: theme.colors.gray[0],
        })}
      >
        {view === 'tree' ? (
          <JsonTree value={value} />
        ) : (
          <Code block sx={{ fontSize: 12, backgroundColor: 'transparent' }}>
            {text}
          </Code>
        )}
      </ScrollArea.Autosize>
    </Box>
  );
}
